import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import timeEntryService from '@api/v1/timeEntryService'
import LoadingSpinner from '@components/common/LoadingSpinner'
import BillingChat from '@components/billing/BillingChat'
import useDocumentTitle from '@hooks/useDocumentTitle'

const TimeEntries = () => {
  useDocumentTitle('Time Entries')
  const queryClient = useQueryClient()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [showCreate, setShowCreate] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['billingEntries', startDate, endDate],
    queryFn: () => timeEntryService.getBillingEntries({
      startDate, endDate, limit: 100,
    }),
  })

  const { data: activeTimer } = useQuery({
    queryKey: ['activeTimer'],
    queryFn: () => timeEntryService.getActiveTimer(),
    refetchInterval: 30000,
  })

  const entries = data?.entries || []
  const totalHours = entries.reduce((sum, e) => sum + e.hours, 0)

  const handleCloseCreate = () => {
    setShowCreate(false)
    queryClient.invalidateQueries({ queryKey: ['billingEntries'] })
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">
            Time Entries
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {entries.length} entr{entries.length !== 1 ? 'ies' : 'y'}
            {' '}&mdash; {totalHours.toFixed(1)} hours
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2.5 bg-cb-accent text-white rounded-lg text-sm
            font-medium hover:bg-cb-accent/90 transition-colors shadow-sm"
        >
          + New Entry
        </button>
      </div>

      {/* Create Panel — AI Quick Entry (same as Billing page) */}
      {showCreate && (
        <div className="mb-6 max-w-xl">
          <div className="h-[420px]">
            <BillingChat onClose={handleCloseCreate} />
          </div>
        </div>
      )}

      {/* Active Timer Banner */}
      {activeTimer && (
        <div className="mb-6 bg-cb-green/10 border border-cb-green/20
          rounded-xl p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-cb-green rounded-full animate-pulse" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                Timer running:{' '}
                {activeTimer['matter-name'] || 'Active Timer'}
              </p>
              <p className="text-xs text-gray-500">
                {activeTimer.description || ''}
              </p>
            </div>
          </div>
          <button
            onClick={() => timeEntryService.stopTimer()}
            className="px-3 py-1.5 bg-cb-orange text-white rounded-lg
              text-xs font-medium hover:bg-cb-orange-dark transition-colors"
          >
            Stop Timer
          </button>
        </div>
      )}

      {/* Date Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500">From</label>
          <input
            type="date"
            aria-label="Start date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-lg
              text-sm text-gray-700 focus:outline-none focus:ring-2
              focus:ring-cb-accent/30 focus:border-cb-accent"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500">To</label>
          <input
            type="date"
            aria-label="End date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-lg
              text-sm text-gray-700 focus:outline-none focus:ring-2
              focus:ring-cb-accent/30 focus:border-cb-accent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100
        overflow-hidden"
      >
        {isLoading ? (
          <div className="p-12 flex justify-center">
            <LoadingSpinner message="Loading time entries..." />
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <p className="text-sm text-red-500">
              Failed to load entries: {error.message}
            </p>
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex
              items-center justify-center mx-auto mb-3"
            >
              <svg
                className="w-6 h-6 text-gray-300" fill="none"
                viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round" strokeLinejoin="round"
                  d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-sm text-gray-500">
              No time entries for this period
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Date</th>
                <th className="text-left px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Matter</th>
                <th className="text-left px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Description</th>
                <th className="text-left px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Code</th>
                <th className="text-right px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Hours</th>
                <th className="text-right px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {entries.map((entry, i) => (
                <tr
                  key={entry.id || i}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-5 py-3.5">
                    <p className="text-sm text-gray-600">
                      {entry.date
                        ? dayjs(entry.date).format('MMM D')
                        : '--'}
                    </p>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-sm font-medium text-gray-900">
                      {entry.matterName || '--'}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 max-w-xs">
                    <p className="text-sm text-gray-600 truncate">
                      {entry.description || entry.name || '--'}
                    </p>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-sm text-gray-600">
                      {entry.billingCode || '--'}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {entry.hours.toFixed(1)}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <p className="text-sm text-gray-600">
                      {entry.billableValue > 0
                        ? `$${entry.billableValue.toFixed(2)}`
                        : '--'}
                    </p>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 bg-gray-50/50">
                <td colSpan={4} className="px-5 py-3">
                  <p className="text-sm font-semibold text-gray-700">
                    Total
                  </p>
                </td>
                <td className="px-5 py-3 text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    {totalHours.toFixed(1)}
                  </p>
                </td>
                <td className="px-5 py-3 text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    ${entries
                      .reduce((sum, e) => sum + e.billableValue, 0)
                      .toFixed(2)}
                  </p>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  )
}

export default TimeEntries
